import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import KineticLines from "../components/motion/KineticLines";
import { Reveal } from "../components/motion/Reveal";
import EditorialMarquee from "../components/site/EditorialMarquee";
import SpotlightImage from "../components/site/SpotlightImage";
import Manifesto from "../components/site/Manifesto";
import ContactBlock from "../components/site/ContactBlock";
import { SERVICES } from "../lib/data/services";
import { CITIES } from "../lib/data/cities";
import { setSEO } from "../lib/data/seo";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  useEffect(() => {
    setSEO({
      title: "British Columbia Security Services — Province-Wide Security Guards, Mobile Patrol & Mining Security",
      description:
        "Licensed BC security guards, mobile patrol, mining security, marine, event, corporate, retail and construction security across all of British Columbia. 50+ cities, 15+ services.",
      keywords: [
        "security services British Columbia",
        "security guards BC",
        "mobile patrol BC",
        "mining security BC",
        "construction security BC",
        "event security BC",
        "concierge security Vancouver",
        "24/7 security British Columbia",
      ],
      canonical: `${window.location.origin}/`,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "SecurityService",
        name: "British Columbia Security Services",
        areaServed: "British Columbia, Canada",
        description:
          "Province-wide security operations covering mobile patrol, static guarding, mining, marine, event, corporate, retail and construction.",
        url: window.location.origin,
      },
    });
  }, []);

  return (
    <div>
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative min-h-[100svh] pt-24 md:pt-32 pb-16 flex flex-col overflow-hidden"
        data-testid="home-hero"
      >
        {/* Parallax background image */}
        <motion.div
          style={{ y: heroY, scale: heroScale, opacity: heroOpacity }}
          className="absolute inset-0 -z-10"
        >
          <img
            src="https://images.pexels.com/photos/8285775/pexels-photo-8285775.jpeg"
            alt="Security officer standing post at night in British Columbia"
            className="w-full h-full object-cover grayscale-[60%] contrast-[1.05]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F4F4F0]/70 via-[#F4F4F0]/30 to-[#F4F4F0]" />
        </motion.div>

        <div className="mx-auto max-w-[1400px] w-full px-6 lg:px-10 flex-1 flex flex-col">
          <div className="mt-8 md:mt-14 flex-1 flex flex-col justify-center">
            <KineticLines
              lines={["Every corner of BC.", "One security operator."]}
              className="font-display text-[28px] leading-[0.88] tracking-[-0.03em] text-[#060913]"
            />

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.9 }}
              className="mt-10 md:mt-14 max-w-xl text-lg md:text-xl text-[#060913]/70 leading-relaxed"
            >
              Mobile patrol, mining, marine, corporate, retail, event, and construction
              security across 50+ British Columbia cities — dispatched from a single
              provincial operations desk.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.9 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-contact-cta"
                className="inline-flex items-center gap-3 border border-[#060913] bg-[#060913] text-[#F4F4F0] px-6 py-4 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-[#D92323] hover:border-[#D92323] transition-colors"
              >
                Contact Us →
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-cta"
                className="inline-flex items-center gap-3 border border-[#060913] px-6 py-4 font-mono text-[11px] tracking-[0.22em] uppercase text-[#060913] hover:bg-[#060913] hover:text-white transition-colors"
              >
                Explore Services
              </Link>
            </motion.div>
          </div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8, duration: 0.6 }}
            className="mt-10 flex items-end justify-between font-mono text-[10px] tracking-[0.28em] uppercase text-[#060913]/60"
          >
            <span>15 Services · 54 Cities · 1 Province</span>
            <span className="hidden md:inline">Scroll ↓</span>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <EditorialMarquee />

      {/* MANIFESTO */}
      <Manifesto />

      {/* SERVICES BENTO */}
      <section
        id="services"
        className="py-24 md:py-40 bg-[#F4F4F0]"
        data-testid="home-services"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div>
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
                Services · 15+
              </div>
              <h2 className="mt-4 font-display text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-[#060913]">
                Full-spectrum
                <br />
                security operations.
              </h2>
            </div>
            <Link
              to="/services"
              className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#060913] border-b border-[#060913] pb-1 hover:text-[#D92323] hover:border-[#D92323]"
            >
              View all services →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {SERVICES.slice(0, 6).map((s, i) => (
              <Reveal
                key={s.slug}
                delay={i * 0.05}
                className={
                  // asymmetric bento
                  i === 0
                    ? "md:col-span-7 md:row-span-2"
                    : i === 1
                    ? "md:col-span-5"
                    : i === 2
                    ? "md:col-span-5"
                    : "md:col-span-4"
                }
              >
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`home-service-card-${s.slug}`}
                  className="group block relative overflow-hidden border border-black/10 hover:border-[#D92323] transition-colors bg-white"
                  style={{ aspectRatio: i === 0 ? "16/13" : "16/10" }}
                >
                  <img
                    src={s.image}
                    alt={s.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#060913]/85 via-[#060913]/30 to-transparent" />
                  <div className="relative h-full flex flex-col justify-between p-6 md:p-8 text-white">
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase opacity-80">
                      {s.number} · Service
                    </div>
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight leading-tight">
                        {s.name}
                      </h3>
                      <p className="mt-2 text-sm md:text-base opacity-80 max-w-md">
                        {s.tagline}
                      </p>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SPOTLIGHT / IMAGE MOMENT */}
      <section className="bg-[#060913] text-[#F4F4F0] py-24 md:py-40" data-testid="home-spotlight">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5 order-2 md:order-1">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              Mining · Feature
            </div>
            <h2 className="mt-6 font-display text-5xl md:text-6xl tracking-tight leading-[0.95]">
              We work where the highway ends.
            </h2>
            <p className="mt-8 text-white/70 text-lg leading-relaxed max-w-lg">
              Copper, gold, coal and molybdenum operations across BC's mineral belt
              rely on hardened, remote-ready officer teams. Fly-in fly-out rotations.
              Pre-cleared for camp accommodation. Trained to your ERP.
            </p>
            <Link
              to="/services/mining-security"
              className="mt-10 inline-flex items-center gap-3 border border-white/40 px-6 py-4 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-[#D92323] hover:border-[#D92323] transition-colors"
            >
              Mining Security →
            </Link>
          </div>
          <div className="md:col-span-7 order-1 md:order-2">
            <SpotlightImage
              src="https://images.unsplash.com/photo-1628487749130-2d41acb1802a"
              alt="Remote mine site in British Columbia at dusk"
              caption="BC · Interior Mineral Belt"
              className="aspect-[4/3]"
            />
          </div>
        </div>
      </section>

      {/* CITIES */}
      <section className="bg-[#F4F4F0] py-24 md:py-40" data-testid="home-cities">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div>
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
                Cities · 50+
              </div>
              <h2 className="mt-4 font-display text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-[#060913]">
                From Whistler to
                <br />
                Fort St. John.
              </h2>
            </div>
            <Link
              to="/cities"
              className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#060913] border-b border-[#060913] pb-1 hover:text-[#D92323] hover:border-[#D92323]"
            >
              View all cities →
            </Link>
          </div>

          <div className="border-t border-black/10">
            {CITIES.slice(0, 10).map((c, i) => (
              <Link
                key={c.slug}
                to={`/cities/${c.slug}`}
                data-testid={`home-city-row-${c.slug}`}
                className="group grid grid-cols-12 gap-4 items-center py-6 border-b border-black/10 hover:bg-white transition-colors"
              >
                <div className="col-span-1 font-mono text-[10px] tracking-[0.24em] text-black/40">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-6 md:col-span-4 font-display text-2xl md:text-4xl lg:text-5xl text-[#060913] group-hover:text-[#D92323] transition-colors">
                  {c.name}
                </div>
                <div className="hidden md:block md:col-span-4 font-mono text-[11px] tracking-[0.2em] uppercase text-[#5C5F66]">
                  {c.region}
                </div>
                <div className="col-span-5 md:col-span-3 text-right font-mono text-[11px] tracking-[0.2em] uppercase text-black/60 group-hover:text-[#D92323]">
                  Explore →
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-[#060913] text-white py-20 md:py-28" data-testid="home-cta-strip">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              Talk To A Human · 24 / 7
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-6xl leading-[0.95] tracking-tight">
              Site survey, custom scope, transparent pricing.
            </h2>
          </div>
          <div className="md:col-span-6">
            <ContactBlock variant="dark" testid="home-cta-contact" />
          </div>
        </div>
      </section>
    </div>
  );
}
